import { dispatcher as fluxDispatcher, reduxDispatcher } from 'lib/Dispatcher';
import { PushEvents } from 'pusher/PusherConstants';
import { getActions } from 'pusher/PusherEventParser';
import PusherClient from './PusherClient';
import EventTracking from 'lib/EventTracking';
import errorLogger from 'utils/errorLogger';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import ApolloPushHandler from 'lib/ApolloPushHandler';
import type { PusherEvent } from './types';

const PUSHER_PUSH_PREFIX = 'pusher:';

let apolloPushHandler: ApolloPushHandler;

if (typeof window !== 'undefined') {
  apolloPushHandler = new ApolloPushHandler({
    client: getApolloClientSingleton(),
  });
}

/* Parse push event data and and dispatch resulting actions.
 * This should really be private, but is exported for testing purpose,
 * hence the definition outside of the PusherActions object.
 */
export function _dispatchPush(push: PusherEvent) {
  const actions = getActions(push);

  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name '__DEV__'.
  if (__DEV__) {
    const tag = actions.map((a) => a.type).join(' - ');
    console.groupCollapsed(`%c📡 PUSH - ${tag}`, 'color: #289647');
    console.log('RECEIVED', push);
    console.log('DISPATCHED', actions);
    console.groupEnd();
  }

  if (actions.length === 0) {
    console.error('dispatchPush - push parsing resulted in empty array of actions', {
      push,
      actions,
    });
    return;
  }

  actions.forEach((action) => {
    fluxDispatcher.dispatch(action);
    reduxDispatcher.dispatch(action);

    if (apolloPushHandler) {
      apolloPushHandler.handle(action);
    }
  });
}

function onPusherPushReceived(type: string, eventData: any) {
  // ignore push not comming from r101-api, like pusher pusher:connection_succeed
  // if you are here because the pusher parser try to parse something
  // it should not have, this is the right place to ignore it.
  if (type.startsWith(PUSHER_PUSH_PREFIX)) {
    return;
  }

  _dispatchPush({ type, eventData });
}

const PusherActions = {
  subscribeToChannel(channelName: string) {
    if (PusherClient.isSubscribedTo(channelName)) {
      return;
    }

    // Have all pusher event go through the dispatcher
    PusherClient.subscribe(channelName)
      .then((channel) => channel.bind_all(onPusherPushReceived))
      .catch(errorLogger('PusherActions subscribeToChannel failed'));

    PusherClient.bind('state_change', this.updateConnectionState);
  },

  unsubscribeToChannel(channelName: string) {
    PusherClient.unsubscribe(channelName);
  },

  updateConnectionState(states: { previous: string; current: string }) {
    fluxDispatcher.dispatch({
      type: PushEvents.CONNECTION_STATE_CHANGED,
      previous: states.previous,
      current: states.current,
    });
    EventTracking.track(`pusher.state_change.${states.previous}.to.${states.current}`);
  },

  retryConnection() {
    PusherClient.retry();
    fluxDispatcher.dispatch({ type: PushEvents.RETRY_CONNECTION });
  },

  resetStore() {
    fluxDispatcher.dispatch({ type: PushEvents.RESET_STORE });
  },
};

// @ts-expect-error ts-migrate(2304) FIXME: Cannot find name '__DEV__'.
if (__DEV__ && typeof window !== 'undefined') {
  /* dev function to dispatch fake push events
   * this is what this function expect for a v3 changset push event:
   *
   * dispatchPush({
   *   type: 'v3.changeset',
   *   eventData: {
   *     changes: [
   *       {
   *         type:   'phone_call',
   *         action: 'update',
   *         data:   { some_data: true }
   *       }
   *     ]
   *   }
   * });
   */
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'dispatchPush' does not exist on type 'Wi... Remove this comment to see the full error message
  window.dispatchPush = _dispatchPush;
}

export default PusherActions;
